import React from "react";
import { AdvancedSearch, AdvancedFilter, Button, Icon } from "@weezevent/nacre";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classes from "./filter.module.scss";
import { getListCount } from "../../../utils/filters";
import GenericFilter from "./filters/GenericFilter";
import StatusFilter from "./filters/StatusFilter";

export const IdentificationType = {
	ALL: "all",
	BARCODE: "barcode",
	SHORT_TAG: "short_tag",
	UID: "uid",
	MEMBER_CODE: "member_code",
	NAME: "name",
	EMAIL: "email"
};

export const IDENTIFICATION_TYPE_TO_PARAM = {
	[IdentificationType.ALL]: "search",
	[IdentificationType.BARCODE]: "search_primary",
	[IdentificationType.SHORT_TAG]: "search_short_tag",
	[IdentificationType.UID]: "search_secondary",
	[IdentificationType.MEMBER_CODE]: "search_member",
	[IdentificationType.NAME]: "search_name",
	[IdentificationType.EMAIL]: "search_email"
};

const Filter = ({
	searchValue,
	onChangeSearchValue,
	searchType,
	onChangeSearchType,
	totalCount,
	onChange,
	initialValues,
	events,
	checkpoints,
	zones,
	periods,
	populations,
	onClickQRCodeButton
}) => {
	const { t } = useTranslation();

	const searchTypes = [
		{
			value: IdentificationType.ALL,
			text: t("access.attendees.attendeesList.filters.searchTypes.all")
		},
		{
			value: IdentificationType.BARCODE,
			text: t(
				"access.attendees.attendeesList.filters.searchTypes.barcode"
			)
		},
		{
			value: IdentificationType.SHORT_TAG,
			text: t(
				"access.attendees.attendeesList.filters.searchTypes.shortTag"
			)
		},
		{
			value: IdentificationType.UID,
			text: t("access.attendees.attendeesList.filters.searchTypes.uid")
		},
		{
			value: IdentificationType.MEMBER_CODE,
			text: t(
				"access.attendees.attendeesList.filters.searchTypes.memberCode"
			)
		},
		{
			value: IdentificationType.NAME,
			text: t("access.attendees.attendeesList.filters.searchTypes.name")
		},
		{
			value: IdentificationType.EMAIL,
			text: t("access.attendees.attendeesList.filters.searchTypes.email")
		}
	];

	const filters = React.useMemo(() => {
		const filterList = [];

		if (events?.length > 0) {
			filterList.push({
				labelName: t(
					"access.attendees.attendeesList.filters.nameLabels.events"
				),
				name: "event_ids",
				component: props => (
					<GenericFilter
						{...props}
						data={events}
						filterKey="event_ids"
					/>
				),
				getCount: getListCount
			});
		}

		if (checkpoints?.length > 0) {
			filterList.push({
				labelName: t(
					"access.attendees.attendeesList.filters.nameLabels.checkpoints"
				),
				name: "checkpoint_ids",
				component: props => (
					<GenericFilter
						{...props}
						data={checkpoints}
						filterKey="checkpoint_ids"
					/>
				),
				getCount: getListCount
			});
		}

		if (zones?.length > 0) {
			filterList.push({
				labelName: t(
					"access.attendees.attendeesList.filters.nameLabels.zones"
				),
				name: "zone_ids",
				component: props => (
					<GenericFilter
						{...props}
						data={zones}
						filterKey="zone_ids"
					/>
				),
				getCount: getListCount
			});
		}

		if (periods?.length > 0) {
			filterList.push({
				labelName: t(
					"access.attendees.attendeesList.filters.nameLabels.periods"
				),
				name: "period_ids",
				component: props => (
					<GenericFilter
						{...props}
						data={periods}
						filterKey="period_ids"
					/>
				),
				getCount: getListCount
			});
		}

		if (populations?.length > 0) {
			filterList.push({
				labelName: t(
					"access.attendees.attendeesList.filters.nameLabels.populations"
				),
				name: "population_ids",
				component: props => (
					<GenericFilter
						{...props}
						data={populations}
						filterKey="population_ids"
					/>
				),
				getCount: getListCount
			});
		}

		filterList.push({
			labelName: t(
				"access.attendees.attendeesList.filters.nameLabels.status"
			),
			name: "status",
			component: StatusFilter,
			getCount: getListCount
		});

		return filterList;
	}, [events, checkpoints, zones, periods, populations, t]);

	return (
		<AdvancedFilter
			value={initialValues || {}}
			onChange={onChange}
			total={{
				label: t("access.attendees.attendeesList.filters.labelTotal", {
					count: totalCount
				}),
				count: totalCount
			}}
			filters={filters}
			onReset={() => onChangeSearchValue(null)}
			leftComponent={
				<div>
					<AdvancedSearch
						value={searchValue}
						onChange={onChangeSearchValue}
						placeholder={t(
							"access.attendees.attendeesList.filters.searchPlaceholder"
						)}
						searchTypes={searchTypes}
						searchType={{
							value: searchType,
							onChange: onChangeSearchType
						}}
					/>
				</div>
			}
			rightComponent={
				<div className={classes.rightSection}>
					<Button
						className={classes.qrButton}
						icon={<Icon name="qr" margin={false} />}
						onClick={onClickQRCodeButton}
					/>
				</div>
			}
		/>
	);
};

Filter.propTypes = {
	onChange: PropTypes.func,
	searchValue: PropTypes.string,
	onChangeSearchValue: PropTypes.func,
	searchType: PropTypes.string,
	onChangeSearchType: PropTypes.func,
	totalCount: PropTypes.number.isRequired,
	events: PropTypes.array.isRequired,
	checkpoints: PropTypes.array.isRequired,
	zones: PropTypes.array,
	periods: PropTypes.array,
	populations: PropTypes.array,
	initialValues: PropTypes.object,
	onClickQRCodeButton: PropTypes.func
};

Filter.defaultProps = {
	onChange: () => {},
	onChangeSearchValue: () => {},
	searchType: IdentificationType.ALL,
	onChangeSearchType: () => {},
	searchValue: null,
	initialValues: {},
	onClickQRCodeButton: () => {}
};

export default Filter;
